package com.arena_web.ui.sections.eventdetail.mobile

import androidx.compose.runtime.*
import com.arena_web.core.viewmodel.EventDetailUiState
import com.arena_web.data.dto.EventParticipantDto
import com.arena_web.data.dto.EventTeamDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.sections.eventdetail.FormatOverridePill
import com.arena_web.ui.sections.eventdetail.StatBlock
import com.arena_web.ui.sections.eventdetail.TeamColorBadge
import com.arena_web.ui.sections.eventdetail.assignPlayersToFormation
import com.arena_web.ui.sections.eventdetail.defaultFormationForFormat
import com.arena_web.ui.sections.eventdetail.parseFormation
import com.arena_web.ui.sections.eventdetail.statBlockKeysForSport
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*

/**
 * Mobile live-match (IN_PROGRESS) screen — Figma 8171:179847 (closed flaps)
 * and 8999:156521 (roster flaps open). Portrait pitch: team 0 defends the top
 * half, team 1 the bottom; the desktop hand-tuned formation coordinates are
 * transposed (desktop runs left→right, mobile top→bottom).
 *
 * Reuses the desktop MatchView machinery: the formation utilities (promoted to
 * `internal`), the tap-to-swap slot state machine, the 1-second timer tick and
 * the stat displayKey mapping. Root sets `--scale: var(--mobile-scale)` so all
 * `s()`-scaled helpers render on the 390-px Figma baseline.
 */
@Composable
fun MatchViewMobile(
    state: EventDetailUiState,
    onBack: () -> Unit,
    onEndMatch: () -> Unit,
    onScoreUp: (teamId: String) -> Unit,
    onScoreDown: (teamId: String) -> Unit,
    onToggleTimer: () -> Unit,
    onResetTimer: () -> Unit,
    onTimerTick: () -> Unit,
    onViewProfile: (userId: String) -> Unit,
    onSetPlayerStat: (userId: String, statTypeId: String, value: Int) -> Unit,
    onSwapLineup: (userIdA: String, userIdB: String) -> Unit,
    onUpdateFormat: (Int) -> Unit,
) {
    val event = state.event ?: return
    val t = LocalAppStrings.current
    val isAdmin = state.isAdmin
    val visibleTeams = event.teams.take(2)

    // Optimistic format override — same layer as desktop MatchView: set
    // immediately for instant UI feedback, PUT via onUpdateFormat, cleared
    // once the server-side value catches up through EVENT_UPDATED.
    var formatOverride by remember { mutableStateOf<Int?>(null) }
    val effectiveFormat = formatOverride ?: event.format ?: 0
    LaunchedEffect(event.format) {
        if (formatOverride == event.format) formatOverride = null
    }

    // Tick the timer every second while running (same pattern as desktop).
    LaunchedEffect(state.timerRunning) {
        if (state.timerRunning) {
            val intervalId = window.setInterval({ onTimerTick() }, 1000)
            kotlinx.coroutines.suspendCancellableCoroutine<Unit> { cont ->
                cont.invokeOnCancellation { window.clearInterval(intervalId) }
            }
        }
    }

    // ── Player action sheet — tapping any card (field or bench drawer) opens
    // a bottom sheet with the big card + stat steppers, "Make substitution"
    // and "View profile". Substitutions replace the old tap-to-swap flow:
    // onSwapLineup fires, the backend broadcasts EVENT_UPDATED, and the cards
    // glide to their new slots via their left/top transitions.
    var actionPlayer by remember { mutableStateOf<EventParticipantDto?>(null) }
    // true → the sheet shows the swap-target list instead of the card.
    var subMode by remember { mutableStateOf(false) }
    // Close the sheet if its player left the event.
    LaunchedEffect(event.participants.map { it.id }) {
        if (actionPlayer != null && event.participants.none { it.id == actionPlayer?.id }) {
            actionPlayer = null
            subMode = false
        }
    }

    // Index (into visibleTeams) of the team whose roster side-sheet is open.
    var openRoster by remember { mutableStateOf<Int?>(null) }
    // Per-team expanded +/- score stepper (index into visibleTeams).
    var expandedScoreTeam by remember { mutableStateOf<Int?>(null) }

    val statTypesByKey = state.playerStats?.statTypes?.associateBy { it.displayKey } ?: emptyMap()
    fun statValue(userId: String, statTypeId: String): Int =
        state.playerStats?.playerStats
            ?.firstOrNull { it.userId == userId && it.statTypeId == statTypeId }?.value ?: 0

    Box(
        modifier = Modifier
            .fillMaxSize()
            .position(Position.Relative)
            .overflow(Overflow.Hidden)
            .styleModifier {
                property("--scale", "var(--mobile-scale)")
                property("min-height", "0")
            }
    ) {
        // ── Ambient pitch backdrop — the same treatment as the Arena screen
        // (ArenaContentMobile, Figma 10567:202821): sport pitch art at low
        // opacity, contained to the viewport width, dissolved into the body
        // composite with a vertical fade mask. Like on Arena, the backdrop's
        // box starts BELOW the header stack — anchoring it at the screen top
        // would push the whole image into the mask's transparent zone.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", "0")
                    property("right", "0")
                    property("top", "calc(env(safe-area-inset-top, 0px) + ${s(105)})")
                    property("bottom", "0")
                    property("pointer-events", "none")
                }
        ) {
            org.jetbrains.compose.web.dom.Img(
                src = AppIcons.mobileEventBackground(event.sportName),
                attrs = Modifier.styleModifier {
                    property("position", "absolute")
                    property("inset", "0")
                    property("width", "100%")
                    property("height", "100%")
                    property("object-fit", "contain")
                    property("object-position", "center 0%")
                    property("pointer-events", "none")
                    val mask = "linear-gradient(180deg, " +
                        "transparent 0%, " +
                        "black 32%, " +
                        "black 78%, " +
                        "transparent 100%)"
                    property("mask-image", mask)
                    property("-webkit-mask-image", mask)
                }.toAttrs()
            )
        }

        // ── Field container — sized to the GRASS area of the backdrop image
        // (contain-fit to the viewport width below the header; the art keeps
        // ~11% side and ~8.5% top/bottom stadium margins), so the formation
        // grid is centred on the drawn pitch. Own z-index makes it a stacking
        // context, so the cards' internal z-order (selected = 500) can't climb
        // above the roster flaps (30).
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .zIndex(10)
                .styleModifier {
                    property("left", s(44))
                    property("width", s(302))
                    property("top", "calc(env(safe-area-inset-top, 0px) + ${s(149)})")
                    property("height", s(432))
                }
        ) {
            visibleTeams.forEachIndexed { teamIdx, team ->
                val formationName = team.formation?.name ?: defaultFormationForFormat(effectiveFormat)
                val rows = parseFormation(formationName)
                val confirmed = event.participants
                    .filter { it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED" }
                val fieldPlayers = confirmed.take(effectiveFormat)
                val assigned = assignPlayersToFormation(fieldPlayers, rows)

                rows.forEachIndexed { rowIdx, count ->
                    for (col in 0 until count) {
                        val slotKey = "$teamIdx-$rowIdx-$col"
                        // Straight top-down grid — desktop's hand-tuned
                        // coordinates bake in that view's sideways perspective
                        // skew, so they don't transpose cleanly. Rows run from
                        // the team's own goal (GK, row 0) toward the half-line;
                        // players in a row spread evenly around the centre.
                        // Small formats (1v1..7v7) pull the TOP team's outfield
                        // rows up, closer to their goal — otherwise the handful
                        // of cards huddles around the half-line. GK stays put,
                        // and the BOTTOM team keeps the original spread (its
                        // cards already read fine low in the frame).
                        val compactBand = effectiveFormat in 1..7 && teamIdx == 0
                        val rowY = when {
                            rows.size == 1 -> 25.0
                            rowIdx == 0 -> 6.0
                            compactBand ->
                                if (rows.size == 2) 20.0
                                else 15.0 + (36.0 - 15.0) * (rowIdx - 1) / (rows.size - 2)
                            else -> 6.0 + (44.0 - 6.0) * rowIdx / (rows.size - 1)
                        }
                        val colX = (col + 1).toDouble() / (count + 1) * 100.0
                        val base =
                            if (teamIdx == 0) colX to rowY
                            else (100.0 - colX) to (100.0 - rowY)
                        val (xPct, yPct) =
                            portraitFormatTweak(effectiveFormat, teamIdx, rowIdx, col, base)

                        val player = assigned.getOrNull(rowIdx)?.getOrNull(col)

                        key(player?.id ?: "empty-$slotKey") {
                            FieldSlot(
                                xPct = xPct,
                                yPct = yPct,
                                player = player,
                                event = event,
                                format = effectiveFormat,
                                onTap = { player?.let { actionPlayer = it } },
                            )
                        }
                    }
                }
            }

        }

        // ── Roster drawers — anchored to the SCREEN edges (not the field
        // container, which is capped at the 390-px baseline and stops short
        // of the right edge on wider phones).
        visibleTeams.forEachIndexed { teamIdx, team ->
            val confirmed = event.participants
                .filter { it.eventTeamId == team.id && it.status.uppercase() == "CONFIRMED" }
            val bench = confirmed.drop(effectiveFormat)
            RosterSidePanel(
                team = team,
                isLeft = teamIdx == 0,
                open = openRoster == teamIdx,
                // The chevron under the tab reads as "cycle teams" — with just
                // the two on screen there is nothing to switch to.
                showCycleChevron = event.teams.size > 2,
                onOpen = { openRoster = teamIdx },
                onClose = { if (openRoster == teamIdx) openRoster = null },
            ) {
                if (bench.isEmpty()) {
                    // Empty state — dimmed team glyph centred in the panel
                    // body, no caption.
                    Box(
                        modifier = Modifier.fillMaxWidth().flexGrow(1).minHeight(0.px),
                        contentAlignment = Alignment.Center,
                    ) {
                        Icon(
                            src = AppIcons.TeamPeople,
                            modifier = Modifier.sWidth(64).sHeight(45).styleModifier {
                                property("opacity", "0.15")
                                property("filter", "brightness(0) invert(1)")
                            },
                        )
                    }
                } else {
                    bench.chunked(2).forEach { rowPlayers ->
                        Row(modifier = Modifier.sGap(10)) {
                            rowPlayers.forEach { player ->
                                key(player.id) {
                                    RosterCard(
                                        player = player,
                                        sport = event.sportName,
                                        onTap = {
                                            actionPlayer = player
                                            openRoster = null
                                        },
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        // ── Player action sheet ──
        actionPlayer?.let { player ->
            BottomSheet(
                onDismiss = { actionPlayer = null; subMode = false },
                contentGap = 16,
            ) {
                if (!subMode) {
                    SpanText(
                        (player.fullName ?: player.username),
                        modifier = Modifier
                            .fillMaxWidth()
                            .sFontSize(18)
                            .fontWeight(FontWeight.Medium)
                            .color(Color("#bed6f5"))
                            .styleModifier { property("text-align", "center") },
                    )

                    // Big card with the desktop corner stat blocks — the same
                    // `StatBlock` composable MatchView uses (card-kit `stat.png`
                    // mini-tiles with icon + value and the slide-out +/− pills).
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPadding(20, 0)
                            // Extra room between the card and the action
                            // buttons below (on top of the sheet's contentGap).
                            .styleModifier { property("margin-bottom", s(12)) },
                        contentAlignment = Alignment.Center,
                    ) {
                        // `other` sport events have no structured scoring —
                        // same skip as desktop MatchView.
                        val showStatBlocks = event.sportName.lowercase() != "other"
                        // StatBlock's inner icon/counter are s()-sized for the
                        // desktop XtraSmall (58×90) field card. Lay the card out
                        // at those same s-units with a local --scale boost
                        // (~150px visual width) so the icon/count-to-tile ratio
                        // matches desktop exactly.
                        Box(
                            modifier = Modifier
                                .sWidth(58)
                                .sHeight(90)
                                .position(Position.Relative)
                                .styleModifier {
                                    property("--scale", "calc(var(--mobile-scale) * 2.6)")
                                },
                        ) {
                            MatchPlayerCard(
                                player = player,
                                sport = event.sportName,
                                // Corner stat blocks crowd the rating shield —
                                // hidden here for the same reason as desktop.
                                hideRating = showStatBlocks,
                            )

                            if (showStatBlocks) {
                                val basePath = player.cardAssetsBasePath ?: "cards/football"
                                val sportLower = event.sportName.lowercase()
                                // Tennis/padel: top-left counts points (0/15/30/40…)
                                // and resets when the top-right games block scores —
                                // mirrors desktop MatchView exactly.
                                val isPointGameSport = sportLower in listOf("tennis", "padel")
                                val topLeftFormatter: (Int) -> String = if (isPointGameSport) {
                                    { c ->
                                        when (c) {
                                            0 -> "0"
                                            1 -> "15"
                                            2 -> "30"
                                            3 -> "40"
                                            else -> (c + 37).toString()
                                        }
                                    }
                                } else {
                                    { it.toString() }
                                }
                                val topLeftIcon =
                                    if (isPointGameSport) AppIcons.StatBlockTarget else AppIcons.StatBlockBall
                                val topLeftIconSize: Number = if (isPointGameSport) 6 else 5
                                val topRightIcon =
                                    if (isPointGameSport) AppIcons.StatBlockBall else AppIcons.StatBlockTarget
                                val topRightIconSize: Number = if (isPointGameSport) 5 else 6

                                val blockKeys = statBlockKeysForSport(event.sportName)
                                fun typeIdForKey(key: String?): String? =
                                    key?.let { statTypesByKey[it]?.id }
                                fun countForKey(key: String?): Int =
                                    typeIdForKey(key)?.let { statValue(player.userId, it) } ?: 0
                                fun push(key: String?, delta: Int) {
                                    val typeId = typeIdForKey(key) ?: return
                                    val current = statValue(player.userId, typeId)
                                    val next = (current + delta).coerceAtLeast(0)
                                    if (next != current) onSetPlayerStat(player.userId, typeId, next)
                                }

                                val tlKey = blockKeys.getOrNull(0)
                                val trKey = blockKeys.getOrNull(1)
                                val blKey = blockKeys.getOrNull(2)
                                val brKey = blockKeys.getOrNull(3)

                                StatBlock(
                                    icon = topLeftIcon,
                                    iconSize = topLeftIconSize,
                                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                                    count = countForKey(tlKey),
                                    onIncrement = { push(tlKey, +1) },
                                    onDecrement = { push(tlKey, -1) },
                                    top = "18%", left = "5%", flipHorizontally = false,
                                    valueFormatter = topLeftFormatter,
                                    steppersAlwaysOpen = true,
                                    stepperOutsetPct = 125,
                                )
                                StatBlock(
                                    icon = topRightIcon,
                                    iconSize = topRightIconSize,
                                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                                    count = countForKey(trKey),
                                    onIncrement = {
                                        push(trKey, +1)
                                        if (isPointGameSport) {
                                            val tlTypeId = typeIdForKey(tlKey)
                                            if (tlTypeId != null && countForKey(tlKey) != 0) {
                                                onSetPlayerStat(player.userId, tlTypeId, 0)
                                            }
                                        }
                                    },
                                    onDecrement = { push(trKey, -1) },
                                    top = "18%", right = "5%", flipHorizontally = true,
                                    steppersAlwaysOpen = true,
                                    stepperOutsetPct = 125,
                                )
                                StatBlock(
                                    iconTint = "#FFFF54",
                                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                                    count = countForKey(blKey),
                                    onIncrement = { push(blKey, +1) },
                                    onDecrement = { push(blKey, -1) },
                                    bottom = "22%", left = "5%", flipHorizontally = false,
                                    steppersAlwaysOpen = true,
                                    stepperOutsetPct = 125,
                                )
                                StatBlock(
                                    iconTint = "#FF3737",
                                    isAdmin = isAdmin, cardAssetsBasePath = basePath,
                                    count = countForKey(brKey),
                                    onIncrement = { push(brKey, +1) },
                                    onDecrement = { push(brKey, -1) },
                                    bottom = "22%", right = "5%", flipHorizontally = true,
                                    steppersAlwaysOpen = true,
                                    stepperOutsetPct = 125,
                                )
                            }
                        }
                    }

                    // Same-team players this one can swap with.
                    val swapTargets = event.participants.filter {
                        it.eventTeamId == player.eventTeamId &&
                            it.status.uppercase() == "CONFIRMED" &&
                            it.id != player.id
                    }
                    if (isAdmin && swapTargets.isNotEmpty()) {
                        SheetActionButton(text = t.eventMakeSubstitution) { subMode = true }
                    }
                    SheetActionButton(text = t.eventViewProfileSheet) {
                        actionPlayer = null
                        subMode = false
                        onViewProfile(player.userId)
                    }
                } else {
                    // ── Substitution target list ──
                    Row(
                        modifier = Modifier.fillMaxWidth().sGap(8),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(
                            modifier = Modifier
                                .sSize(32)
                                .cursor(Cursor.Pointer)
                                .onClick { subMode = false }
                                .styleModifier {
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                },
                        ) {
                            Icon(src = AppIcons.ChevronBack, modifier = Modifier.sSize(18))
                        }
                        SpanText(
                            t.eventSwapWith(player.fullName ?: player.username),
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.Medium)
                                .color(Color("#bed6f5"))
                                .styleModifier {
                                    property("white-space", "nowrap")
                                    property("overflow", "hidden")
                                    property("text-overflow", "ellipsis")
                                    property("min-width", "0")
                                },
                        )
                    }
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sGap(4)
                            .sMaxHeight(380)
                            .styleModifier {
                                property("overflow-y", "auto")
                                property("scrollbar-width", "none")
                            },
                    ) {
                        val swapTargets = event.participants.filter {
                            it.eventTeamId == player.eventTeamId &&
                                it.status.uppercase() == "CONFIRMED" &&
                                it.id != player.id
                        }
                        swapTargets.forEach { target ->
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .sBorderRadius(12)
                                    .sPadding(8, 10)
                                    .cursor(Cursor.Pointer)
                                    .onClick {
                                        onSwapLineup(player.userId, target.userId)
                                        actionPlayer = null
                                        subMode = false
                                    }
                                    .sGap(12),
                                verticalAlignment = Alignment.CenterVertically,
                            ) {
                                Box(
                                    modifier = Modifier
                                        .sSize(40)
                                        .sBorderRadius(100)
                                        .overflow(Overflow.Hidden)
                                        .flexShrink(0)
                                        .styleModifier { property("background", "rgba(255,255,255,0.1)") },
                                    contentAlignment = Alignment.Center,
                                ) {
                                    Image(
                                        src = target.cardAvatarUrl ?: target.avatarUrl ?: AppIcons.EmptyAvatar,
                                        modifier = Modifier.fillMaxSize()
                                            .styleModifier { property("object-fit", "cover") },
                                    )
                                }
                                Column(modifier = Modifier.flexGrow(1).sGap(2)) {
                                    SpanText(
                                        target.fullName ?: target.username,
                                        modifier = Modifier
                                            .sFontSize(15)
                                            .fontWeight(FontWeight.Medium)
                                            .color(AppColors.PrimaryWhite),
                                    )
                                    target.position?.takeIf { it.isNotBlank() }?.let { pos ->
                                        SpanText(
                                            pos.uppercase(),
                                            modifier = Modifier.sFontSize(11).color(com.arena_web.ui.theme.ThemeVars.Text2),
                                        )
                                    }
                                }
                                Icon(
                                    src = AppIcons.ChevronForward,
                                    modifier = Modifier.sSize(14).styleModifier { property("opacity", "0.5") },
                                )
                            }
                        }
                    }
                }
            }
        }

        // ── Header (flows above everything) ──
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .position(Position.Relative)
                .zIndex(40)
                .sGap(24)
                .styleModifier {
                    property("padding-top", "calc(env(safe-area-inset-top, 0px) + ${s(12)})")
                    property("padding-left", "16px")
                    property("padding-right", "16px")
                    property("box-sizing", "border-box")
                    property("pointer-events", "none")
                }
        ) {
            // Back + End match
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("pointer-events", "none") },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(modifier = Modifier.styleModifier {
                    property("pointer-events", "auto")
                    // The header's gutter is 16px, but the loading screen's back
                    // row (and the event-detail hero overlay) sit at s(12) —
                    // nudge only the back button so it doesn't jump sideways
                    // once the IN_PROGRESS content replaces the spinner.
                    property("margin-left", "calc(${s(12)} - 16px)")
                }) {
                    MatchCircleButton(icon = AppIcons.ChevronBack, onClick = onBack)
                }
                if (isAdmin) {
                    Box(
                        modifier = Modifier
                            .sHeight(44)
                            .sBorderRadius(22)
                            .sPadding(8, 22)
                            .cursor(Cursor.Pointer)
                            .onClick { onEndMatch() }
                            .styleModifier {
                                property("background-color", "#c6590c")
                                property("pointer-events", "auto")
                                property("box-sizing", "border-box")
                            },
                        contentAlignment = Alignment.Center,
                    ) {
                        SpanText(
                            t.eventEndMatch,
                            modifier = Modifier
                                .sFontSize(16)
                                .fontWeight(FontWeight.Medium)
                                .color(AppColors.PrimaryWhite)
                                .styleModifier { property("white-space", "nowrap") },
                        )
                    }
                }
            }

            // Scoreboard + Pro Stat toggle
            Row(
                modifier = Modifier.fillMaxWidth().styleModifier { property("pointer-events", "none") },
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.Top,
            ) {
                Box(modifier = Modifier.styleModifier { property("pointer-events", "auto") }) {
                    MobileScoreboard(
                        teams = visibleTeams,
                        timerSeconds = state.timerSeconds,
                        timerRunning = state.timerRunning,
                        isAdmin = isAdmin,
                        expandedScoreTeam = expandedScoreTeam,
                        onExpandScoreTeam = { expandedScoreTeam = it },
                        onScoreUp = onScoreUp,
                        onScoreDown = onScoreDown,
                        onToggleTimer = onToggleTimer,
                        onResetTimer = onResetTimer,
                    )
                }
                if (isAdmin) {
                    Column(
                        modifier = Modifier.sGap(8).styleModifier { property("pointer-events", "auto") },
                        horizontalAlignment = Alignment.End,
                    ) {
                        // Format switcher — same control as desktop's TopBar
                        // (FormatOverridePill), optimistic override + PUT.
                        FormatOverridePill(
                            overrideFormat = formatOverride,
                            onFormatChange = { picked ->
                                if (picked != null && picked != event.format) {
                                    formatOverride = picked
                                    onUpdateFormat(picked)
                                } else {
                                    formatOverride = null
                                }
                            },
                            sportName = event.sportName,
                            fallbackFormat = event.format,
                            showLabel = false,
                            alignDropdownEnd = true,
                            dropdownMinWidth = 110,
                        )
                    }
                }
            }
        }
    }
}

/**
 * Per-format position tweaks reviewed by eye on top of the automatic grid.
 * Row indices follow the default formations (GK is always row 0):
 * 2v2 [1,1] · 3v3 [1,2] · 4v4 [1,2,1] · 5v5 [1,2,1,1] · 6v6 [1,2,2,1].
 */
private fun portraitFormatTweak(
    format: Int,
    teamIdx: Int,
    rowIdx: Int,
    col: Int,
    base: Pair<Double, Double>,
): Pair<Double, Double> {
    val (x, y) = base
    return when (format) {
        // Both players side by side instead of stacked (mirrored below); the
        // rows sit pulled in toward the half-line.
        2 -> when {
            teamIdx == 0 && rowIdx == 0 -> 32.0 to 25.0
            teamIdx == 0 -> 68.0 to 25.0
            rowIdx == 0 -> 68.0 to 75.0
            else -> 32.0 to 75.0
        }
        // Top: trio lower with a wider GK↔pair gap; bottom: GK pulled up off
        // the goal line, attackers dropped slightly.
        3 -> when {
            teamIdx == 0 && rowIdx == 0 -> x to 11.0
            teamIdx == 0 -> x to 30.0
            rowIdx == 0 -> x to 90.0
            else -> x to 60.0
        }
        // Top defenders a touch lower.
        4 -> if (teamIdx == 0 && rowIdx == 1) x to (y + 5.0) else base
        // Mid+striker become one forward ROW on BOTH sides (they land stacked
        // in a column otherwise); the top defenders and forwards sit a bit
        // lower than the automatic band.
        5 -> when {
            teamIdx == 0 && rowIdx == 0 -> x to 9.0
            teamIdx == 0 && rowIdx == 1 -> x to 21.0
            teamIdx == 0 && rowIdx == 2 -> 38.0 to 37.0
            teamIdx == 0 && rowIdx == 3 -> 62.0 to 37.0
            teamIdx == 1 && rowIdx == 2 -> 62.0 to 63.0
            teamIdx == 1 && rowIdx == 3 -> 38.0 to 63.0
            else -> base
        }
        // Midfield pairs converge on the half-line (bottom pair a bit higher);
        // the top mids and striker sit lower than the automatic band.
        6 -> when {
            teamIdx == 0 && rowIdx == 2 -> x to (y + 6.0)
            teamIdx == 0 && rowIdx == 3 -> x to (y + 3.0)
            teamIdx == 1 && rowIdx == 2 -> x to (y - 3.0)
            else -> base
        }
        // Top striker a bit lower; the bottom CENTRE mid (middle of the three)
        // drops out of its line toward the goal.
        7 -> when {
            teamIdx == 0 && rowIdx == 3 -> x to (y + 3.0)
            teamIdx == 1 && rowIdx == 2 && col == 1 -> x to (y + 3.5)
            else -> base
        }
        else -> base
    }
}

/** Width (390-baseline px) of a field card. Anchored by eye: 1v1/2v2 keep a
 *  large card, 5v5 sits at 42px, and the shrink flattens out towards 26px at
 *  11v11 so a full formation reads on a 390px-wide pitch without overlaps. */
private fun fieldCardWidth(format: Int): Double = when {
    format <= 2 -> 54.0
    format <= 5 -> 54.0 - (format - 2) * 4.0
    format >= 11 -> 26.0
    else -> 42.0 - (format - 5) * (16.0 / 6.0)
}

// ── Field slot ────────────────────────────────────────────────────────────────

@Composable
private fun FieldSlot(
    xPct: Double,
    yPct: Double,
    player: EventParticipantDto?,
    event: com.arena_web.data.dto.EventDetailDto,
    format: Int,
    onTap: () -> Unit,
) {
    val cardW = fieldCardWidth(format)
    val cardH = cardW * 147.0 / 94.0

    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .styleModifier {
                property("left", "$xPct%")
                property("top", "$yPct%")
                property("transform", "translate(-50%, -50%)")
                // Server-driven swaps re-slot the players; the position
                // transition makes the affected cards glide across the pitch.
                property(
                    "transition",
                    "left 2.0s cubic-bezier(0.22, 1, 0.36, 1)," +
                        " top 2.0s cubic-bezier(0.22, 1, 0.36, 1)",
                )
                property("z-index", "50")
                property("cursor", "pointer")
                property("user-select", "none")
                property("-webkit-user-select", "none")
            }
            .onClick { it.stopPropagation(); onTap() }
    ) {
        Box(
            modifier = Modifier
                .sWidth(cardW)
                .sHeight(cardH)
                .position(Position.Relative),
        ) {
            if (player != null) {
                MatchPlayerCard(player = player, sport = event.sportName)
            } else {
                // Empty slot — dimmed blank card art, same footprint.
                Box(
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("filter", "brightness(0.35)")
                        property(
                            "background",
                            "url('https://images.arenaproplay.com/football_card.png') center/contain no-repeat",
                        )
                    }
                )
            }
        }
    }
}

/** The standard player card filling its parent box (or the avatar fallback
 *  when the player has no card assets). */
@Composable
private fun MatchPlayerCard(
    player: EventParticipantDto,
    sport: String,
    hideRating: Boolean = false,
) {
    if (player.cardAssetsBasePath != null) {
        PlayerCard(
            playerName = (player.fullName?.split(" ")?.lastOrNull() ?: player.username).uppercase(),
            rating = player.overallRating.toInt(),
            position = player.position ?: "",
            sport = sport,
            size = PlayerCardSize.Arena,
            avatarUrl = player.cardAvatarUrl ?: AppIcons.EmptyAvatar,
            cardAssetsBasePath = player.cardAssetsBasePath,
            cardTextColor = player.cardTextColor,
            hideRating = hideRating,
            fillContainer = true,
            // The action sheet boosts the local --scale; the fit-scale must
            // divide by the same var or the card art blows past its slot.
            fillContainerScaleAware = true,
        )
    } else {
        PlayerCard(
            playerName = (player.fullName?.split(" ")?.lastOrNull() ?: player.username).uppercase(),
            sport = sport,
            size = PlayerCardSize.Arena,
            avatarUrl = player.avatarUrl ?: AppIcons.EmptyAvatar,
            hideRating = true,
            fillContainer = true,
            fillContainerScaleAware = true,
        )
    }
}

// ── Scoreboard ────────────────────────────────────────────────────────────────

@Composable
private fun MobileScoreboard(
    teams: List<EventTeamDto>,
    timerSeconds: Long,
    timerRunning: Boolean,
    isAdmin: Boolean,
    expandedScoreTeam: Int?,
    onExpandScoreTeam: (Int?) -> Unit,
    onScoreUp: (teamId: String) -> Unit,
    onScoreDown: (teamId: String) -> Unit,
    onToggleTimer: () -> Unit,
    onResetTimer: () -> Unit,
) {
    val t = LocalAppStrings.current
    val mm = (timerSeconds / 60).toString().padStart(2, '0')
    val ss = (timerSeconds % 60).toString().padStart(2, '0')

    Row(verticalAlignment = Alignment.Top) {
        Column(modifier = Modifier.sWidth(138).position(Position.Relative)) {
            // Score panel — light plate with the two team rows.
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .sGap(8)
                    .sPadding(6, 6)
                    .position(Position.Relative)
                    .styleModifier {
                        property("background-color", "#ebebeb")
                        property("border-radius", "${s(8)} 0 0 0")
                        property("box-sizing", "border-box")
                    },
            ) {
                teams.forEach { team -> ScoreRow(team) }
                // Broken vertical divider between names and scores.
                Column(
                    modifier = Modifier
                        .position(Position.Absolute)
                        .styleModifier {
                            property("left", s(97))
                            property("top", s(8))
                            property("bottom", s(8))
                            property("justify-content", "space-between")
                        },
                ) {
                    Box(modifier = Modifier.sWidth(2).sHeight(12).backgroundColor(Color.rgb(0, 0, 0)))
                    Box(modifier = Modifier.sWidth(2).sHeight(12).backgroundColor(Color.rgb(0, 0, 0)))
                }
            }

            // Timer bar.
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .sHeight(30)
                    .sPaddingLeft(8)
                    .position(Position.Relative)
                    .styleModifier {
                        property("background-color", "#000000")
                        property("border-radius", "0 0 ${s(8)} 0")
                        property("box-sizing", "border-box")
                    },
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SpanText(
                    "$mm : $ss",
                    modifier = Modifier
                        .sFontSize(18)
                        .fontWeight(FontWeight.Medium)
                        .color(AppColors.PrimaryWhite)
                        .styleModifier { property("letter-spacing", s(0.2)) },
                )
                if (isAdmin) {
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sSize(24)
                            .sBorderRadius(100)
                            .cursor(Cursor.Pointer)
                            .onClick { onToggleTimer() }
                            .styleModifier {
                                property("left", s(97))
                                property("top", "50%")
                                property("transform", "translateY(-50%)")
                                property("background-color", "#0061ff")
                            },
                        contentAlignment = Alignment.Center,
                    ) {
                        Icon(
                            src = if (timerRunning) AppIcons.PauseSmall else AppIcons.Play,
                            modifier = Modifier.sSize(12),
                        )
                    }
                }
            }

            // Reset pill hanging under the timer bar (admin only).
            if (isAdmin) {
                Box(
                    modifier = Modifier
                        .sWidth(93)
                        .sHeight(34)
                        .cursor(Cursor.Pointer)
                        .onClick { onResetTimer() }
                        .styleModifier {
                            property("background-color", "#0061ff")
                            property("border-radius", "0 0 ${s(10)} ${s(10)}")
                            property("border", "0.3px solid rgba(255,255,255,0.4)")
                            property("border-top", "none")
                            property("box-sizing", "border-box")
                        },
                    contentAlignment = Alignment.Center,
                ) {
                    SpanText(
                        t.eventReset,
                        modifier = Modifier
                            .sFontSize(15)
                            .fontWeight(FontWeight.Medium)
                            .color(AppColors.PrimaryWhite),
                    )
                }
            }
        }

        // Score chevron tabs — one per team; tapping opens a +/- stepper.
        if (isAdmin) {
            // No top padding / gap — tab centres (h 34) then track the score
            // rows (24 + 8 pitch) to within 1px.
            Column(verticalArrangement = Arrangement.Top) {
                teams.forEachIndexed { idx, team ->
                    Box(modifier = Modifier.position(Position.Relative)) {
                        Box(
                            modifier = Modifier
                                .sWidth(24)
                                .sHeight(34)
                                .cursor(Cursor.Pointer)
                                .onClick { onExpandScoreTeam(if (expandedScoreTeam == idx) null else idx) }
                                .styleModifier {
                                    property("border-radius", "0 ${s(8)} ${s(8)} 0")
                                    property("border", "0.3px solid rgba(255,255,255,0.4)")
                                    property("border-left", "none")
                                    property(
                                        "background",
                                        "linear-gradient(90deg, rgba(102,102,102,0) 30%, rgba(12,66,255,0.42) 97%)",
                                    )
                                    property("box-sizing", "border-box")
                                },
                            contentAlignment = Alignment.Center,
                        ) {
                            Icon(
                                src = AppIcons.ChevronForward,
                                modifier = Modifier.sSize(16).styleModifier {
                                    if (expandedScoreTeam == idx) property("transform", "rotate(180deg)")
                                },
                            )
                        }
                        if (expandedScoreTeam == idx) {
                            // Same polished +/− pill as the stat steppers next
                            // to the player card in the action sheet — one
                            // vertical `ic_add_stat.png`, top half +1, bottom
                            // half −1.
                            Box(
                                modifier = Modifier
                                    .position(Position.Absolute)
                                    .sWidth(36)
                                    .sHeight(78)
                                    .styleModifier {
                                        property("left", "calc(100% + ${s(12)})")
                                        property("top", "50%")
                                        property("transform", "translateY(-50%)")
                                        property("z-index", "10")
                                        property(
                                            "background",
                                            "url('/icons/ic_add_stat.png') center/contain no-repeat",
                                        )
                                    },
                            ) {
                                ScoreStepHalf(isPlus = true) { onScoreUp(team.id) }
                                ScoreStepHalf(isPlus = false) { onScoreDown(team.id) }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun ScoreRow(team: EventTeamDto) {
    Row(
        modifier = Modifier.fillMaxWidth().sHeight(24).sPaddingRight(6).position(Position.Relative),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            team.name.take(4).uppercase(),
            modifier = Modifier
                .sFontSize(18)
                .fontWeight(FontWeight.Bold)
                .color(Color.rgb(0, 0, 0))
                .styleModifier {
                    property("letter-spacing", s(0.09))
                    property("white-space", "nowrap")
                },
        )
        // Logo hugs the divider (panel divider sits at left 97; row content
        // starts at 6), not the team name.
        Box(
            modifier = Modifier
                .position(Position.Absolute)
                .styleModifier {
                    property("left", s(63))
                    property("top", "50%")
                    property("transform", "translateY(-50%)")
                },
        ) {
            if (team.officialTeamImageUrl != null) {
                Image(
                    src = team.officialTeamImageUrl,
                    modifier = Modifier.sSize(24).styleModifier { property("object-fit", "contain") },
                )
            } else {
                TeamColorBadge(color = team.color ?: "#0D184A", size = 20)
            }
        }
        SpanText(
            "${team.score}",
            modifier = Modifier
                .sFontSize(20)
                .fontWeight(FontWeight.SemiBold)
                .color(Color.rgb(0, 0, 0)),
        )
    }
}

/** Invisible tap half of the score +/− pill — the `ic_add_stat.png` backdrop
 *  on the parent draws the glyphs; this only carries the hit zone. */
@Composable
private fun ScoreStepHalf(isPlus: Boolean, onClick: () -> Unit) {
    // Native `pointerdown` in a ref closure instead of Compose `.onClick`:
    // every tap patches `event.teams`, which recomposes the whole match view,
    // and compose-html re-registers `.onClick` listeners on each recomposition
    // — rapid taps landing in that window are silently lost (same reason the
    // feed's double-tap like uses native listeners). Firing on the touch
    // itself also skips the browser's ~300ms double-tap click suppression.
    val latestClick by rememberUpdatedState(onClick)
    Box(
        modifier = Modifier
            .position(Position.Absolute)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property(if (isPlus) "top" else "bottom", "0")
                property("left", "0")
                property("right", "0")
                property("height", "50%")
                property("touch-action", "manipulation")
                property("user-select", "none")
                property("-webkit-user-select", "none")
                property("-webkit-tap-highlight-color", "transparent")
            }
            .attrsModifier {
                ref { el ->
                    val handler: (org.w3c.dom.events.Event) -> Unit = { e ->
                        e.stopPropagation()
                        // Cancels the follow-up compatibility mouse events, so
                        // this never double-fires alongside a synthetic click.
                        e.preventDefault()
                        latestClick()
                    }
                    el.addEventListener("pointerdown", handler)
                    onDispose { el.removeEventListener("pointerdown", handler) }
                }
            },
    ) {}
}

// ── Edge tabs (closed state) ──────────────────────────────────────────────────

/** Live bridge between the panel's touch listeners and the current state —
 *  the ref closure is wired once, so it reads these fields at event time. */
private class RosterPanelSwipe {
    var open = false
    var onOpen: () -> Unit = {}
    var onClose: () -> Unit = {}
}

// Roster drawer geometry (390-baseline px).
private const val ROSTER_PANEL_W = 200
private const val ROSTER_PANEL_H = 336

/**
 * Roster drawer — the small edge tab with the team logo is the visible TIP of
 * a full panel hidden past the screen edge. Tap (or inward swipe) slides the
 * whole assembly in — the tab rides along on the panel's inner edge, staying
 * its handle; tapping it again (or an outward swipe) slides everything back
 * until only the tab pokes out. Panel and tab share the same fill with no
 * borders and a 2px overlap, so they read as one solid silhouette. One
 * composited translateX drives it all.
 */
@Composable
private fun RosterSidePanel(
    team: EventTeamDto,
    isLeft: Boolean,
    open: Boolean,
    showCycleChevron: Boolean,
    onOpen: () -> Unit,
    onClose: () -> Unit,
    content: @Composable () -> Unit,
) {
    val swipe = remember { RosterPanelSwipe() }
    swipe.open = open
    swipe.onOpen = onOpen
    swipe.onClose = onClose

    // Wrapper = panel + attached tab, anchored at the screen edge. Closed it
    // is shifted out by exactly the panel width, leaving only the tab visible.
    Row(
        modifier = Modifier
            .position(Position.Absolute)
            .attrsModifier {
                ref { el ->
                    var sx = 0.0
                    var sy = 0.0
                    var fired = false
                    val down: (org.w3c.dom.events.Event) -> Unit = { e ->
                        val t = e.asDynamic().touches[0]
                        sx = (t.clientX as Number).toDouble()
                        sy = (t.clientY as Number).toDouble()
                        fired = false
                    }
                    val move: (org.w3c.dom.events.Event) -> Unit = { e ->
                        if (!fired) {
                            val t = e.asDynamic().touches[0]
                            val dx = (t.clientX as Number).toDouble() - sx
                            val dy = (t.clientY as Number).toDouble() - sy
                            if (kotlin.math.abs(dx) > 15 && kotlin.math.abs(dx) > kotlin.math.abs(dy)) {
                                val inward = if (isLeft) dx > 0 else dx < 0
                                fired = true
                                if (inward && !swipe.open) swipe.onOpen()
                                else if (!inward && swipe.open) swipe.onClose()
                            }
                        }
                    }
                    el.addEventListener("touchstart", down)
                    el.addEventListener("touchmove", move)
                    onDispose {
                        el.removeEventListener("touchstart", down)
                        el.removeEventListener("touchmove", move)
                    }
                }
            }
            .styleModifier {
                // Flush with the true screen edge (the drawer lives at the
                // ROOT, not in the 390-capped field container); −2 hides the
                // tab vector's flat-edge sliver.
                property(if (isLeft) "left" else "right", s(-2))
                // Vertical centre of the field area (top safe+180, height 540).
                // Vertical centre of the pitch grass area (top safe+149, h 432).
                property("top", "calc(env(safe-area-inset-top, 0px) + ${s(365)})")
                val slide = "calc(${if (isLeft) "-1" else "1"} * ${s(ROSTER_PANEL_W)})"
                property(
                    "transform",
                    "translateY(-50%) translateX(${if (open) "0px" else slide})",
                )
                // Slow, soft glide — long ease-out tail so the drawer settles
                // gently instead of snapping into place.
                property("transition", "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)")
                // Above every field card (their max z is 500) so the slid-in
                // panel covers the formation underneath.
                property("z-index", if (open) "600" else "20")
                property("touch-action", "pan-y")
            },
        verticalAlignment = Alignment.CenterVertically,
    ) {
        val panel: @Composable () -> Unit = {
            Column(
                modifier = Modifier
                    .sWidth(ROSTER_PANEL_W)
                    .sHeight(ROSTER_PANEL_H)
                    .flexShrink(0)
                    .sGap(10)
                    .sPadding(12, 12)
                    .styleModifier {
                        // Same fill as the tab vector and NO border — the tab
                        // docks flush into this edge, so panel + tab read as
                        // one solid silhouette.
                        property("background-color", "var(--l-surface-2, var(--l-s2, #09142D))")
                        val r = s(16)
                        property("border-radius", if (isLeft) "0 $r $r 0" else "$r 0 0 $r")
                        property("box-sizing", "border-box")
                    },
            ) {
                // Roster grid — scrolls when the squad outgrows the panel.
                // (No header — the team is identified by the crest on the tab.)
                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .flexGrow(1)
                        .minHeight(0.px)
                        .sGap(10)
                        .styleModifier {
                            property("overflow-y", "auto")
                            property("scrollbar-width", "none")
                        },
                ) {
                    content()
                }
            }
        }

        val tab: @Composable () -> Unit = {
            // The handle — flat side docks against the panel, bulge points
            // into the pitch. Tap toggles the drawer.
            Box(
                modifier = Modifier
                    .sWidth(45)
                    .sHeight(112)
                    .flexShrink(0)
                    .position(Position.Relative)
                    .cursor(Cursor.Pointer)
                    .onClick { it.stopPropagation(); if (open) onClose() else onOpen() }
                    .styleModifier {
                        // Tuck the tab's flat edge 2px INTO the panel so the
                        // two fills merge with no hairline gap at any zoom.
                        property("margin-${if (isLeft) "left" else "right"}", s(-2))
                    },
                contentAlignment = Alignment.Center,
            ) {
                // The vector's flat edge is on the left (ready-made for the
                // left drawer); the right one mirrors it.
                Box(
                    modifier = Modifier.position(Position.Absolute).styleModifier {
                        property("inset", "0")
                        property(
                            "background",
                            "url('/images/mobile-match-tab.svg') center/100% 100% no-repeat",
                        )
                        if (!isLeft) property("transform", "scale(-1, 1)")
                        property("pointer-events", "none")
                    }
                )
                // Team chip — dark circle with the crest / colour badge.
                Box(
                    modifier = Modifier
                        .sSize(38)
                        .sBorderRadius(100)
                        .overflow(Overflow.Hidden)
                        .styleModifier {
                            property("background-color", "#0a1128")
                            property("border", "0.3px solid rgba(255,255,255,0.25)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        },
                ) {
                    TeamChipGraphic(team = team, size = 26)
                }
                // Chevron below the tab (Figma 8999:157298) — hidden once the
                // drawer is out, the open panel is its own affordance.
                if (!open && showCycleChevron) {
                    Box(
                        modifier = Modifier
                            .position(Position.Absolute)
                            .sSize(44)
                            .styleModifier {
                                property("top", "calc(100% + ${s(2)})")
                                property("left", "50%")
                                property("transform", "translateX(-50%)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                            },
                    ) {
                        Icon(src = AppIcons.ChevronDown, modifier = Modifier.sSize(24))
                    }
                }
            }
        }

        // Panel sits toward the screen edge, the tab handle on its inner side.
        if (isLeft) { panel(); tab() } else { tab(); panel() }
    }
}

/** Team crest image, or the colour badge fallback. */
@Composable
private fun TeamChipGraphic(team: EventTeamDto, size: Int) {
    if (team.officialTeamImageUrl != null) {
        Image(
            src = team.officialTeamImageUrl,
            modifier = Modifier.sSize(size).styleModifier { property("object-fit", "contain") },
        )
    } else {
        TeamColorBadge(color = team.color ?: "#0D184A", size = size)
    }
}

/** Full-width pill button inside the player action sheet — project button
 *  tokens: InteractiveSecondary fill + gradient hairline border. */
@Composable
private fun SheetActionButton(text: String, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .sHeight(48)
            .sBorderRadius(100)
            .cursor(Cursor.Pointer)
            .onClick { it.stopPropagation(); onClick() }
            .styleModifier {
                property("border", "0.3px solid transparent")
                property(
                    "background",
                    "${com.arena_web.ui.theme.AppGradients.InteractiveSecondary} padding-box, " +
                        "linear-gradient(0deg, var(--l-s2, #09142d), var(--l-s2, #09142d)) padding-box, " +
                        "${com.arena_web.ui.theme.AppGradients.ButtonBorderGradientPrimary} border-box",
                )
                property("box-sizing", "border-box")
            },
        contentAlignment = Alignment.Center,
    ) {
        SpanText(
            text,
            modifier = Modifier
                .sFontSize(16)
                .fontWeight(FontWeight.Medium)
                .color(Color("#bed6f5")),
        )
    }
}

// ── Roster card inside the side-sheet ─────────────────────────────────────────

@Composable
private fun RosterCard(
    player: EventParticipantDto?,
    sport: String,
    onTap: () -> Unit,
) {
    Box(
        modifier = Modifier
            .sWidth(66)
            .sHeight(104)
            .cursor(Cursor.Pointer)
            .onClick { it.stopPropagation(); onTap() },
    ) {
        if (player != null) {
            MatchPlayerCard(player = player, sport = sport)
        } else {
            Box(
                modifier = Modifier.fillMaxSize().styleModifier {
                    property("filter", "brightness(0.35)")
                    property(
                        "background",
                        "url('https://images.arenaproplay.com/football_card.png') center/contain no-repeat",
                    )
                }
            )
        }
    }
}

// ── Shared circular icon button (page-composite backing, as on the reg screen) ──

@Composable
private fun MatchCircleButton(icon: String, onClick: () -> Unit) {
    // Mirrors EventDetailContentMobile's CircleIconButton exactly (fill +
    // mask-composite gradient ring) — the loading screen and the hero overlay
    // draw the back button with it, so any styling drift here shows up as the
    // button's border visibly changing once the match view mounts.
    Box(
        modifier = Modifier
            .sSize(44)
            .sBorderRadius(100)
            .position(Position.Relative)
            .cursor(Cursor.Pointer)
            .onClick { onClick() }
            .styleModifier {
                property(
                    "background",
                    "linear-gradient(24deg, rgba(0, 59, 174, 0.08) 27%, rgba(3, 87, 230, 0.08) 68%)",
                )
            },
        contentAlignment = Alignment.Center,
    ) {
        Box(
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("border", "0.3px solid transparent")
                property("border-radius", "100px")
                property(
                    "background",
                    "${com.arena_web.ui.theme.AppGradients.ButtonBorderGradientPrimary} border-box",
                )
                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                property("-webkit-mask-composite", "xor")
                property("mask-composite", "exclude")
                property("pointer-events", "none")
            }
        ) {}
        Icon(src = icon, modifier = Modifier.sSize(18))
    }
}
